import {type CanvasBarPosition} from '@packages/systems/designer/ui';

export const topBarMinHeight = 33;
export const previewLinkSignupBannerHeight = 36;
export const leftSidebarWidth = 40;
export const leftSidebarWidthComfortable = 68;

export const rightSidebarLabelWidth = 44;
export const rightSidebarWidthInner = 240;
export const rightSidebarWidthInnerLarge = 360;
export const rightSidebarBorder = 1;
export const rightSidebarWidth = rightSidebarWidthInner + rightSidebarBorder;
export const rightSidebarWidthLarge =
  rightSidebarWidthInnerLarge + rightSidebarBorder;
export const rightSidebarHeaderHeight = 36;
export const rightSidebarTabHeight = 41;
export const viewportMinWidth = 900;
export const truncatedDomainNameLength = 27;
export const assetDropZoneHeight = 107;
export const assetManagerGridTop = 46;
export const autoscrollThreshold = 30;
export const maxScrollSpeed = 12;
export const navBetaSwitchHeight = 24;
export const leftPanelDefaultSize = 250;
export const leftPanelLargeSize = 650;
export const leftPanelGapWidth = 7;
export const leftPanelBorder = 1;

export const IX2_TABS_MIN_HEIGHT = 200;

/**
 * Default max height for virtualized SearchableListBox in selector popovers.
 * This value shows approximately 10 items (320px for 10 × 32px items)
 * plus an additional 7px to display a partial 11th item, which provides
 * a visual affordance that more content is available to scroll.
 */
export const SEARCHABLE_LIST_BOX_VIRTUALIZED_MAX_HEIGHT = 327;

export const DESIGNER_USER_TYPES = Object.freeze({
  AUTHORIZED_IN_DESIGNER: 'authorizedInDesigner', // authorized Designer user
  UNAUTHORIZED: 'unauthorized', // read-only, playground
  ANONYMOUS_COMMENT: 'anonymousComment', // anonymous commenting
});

export type DesignerUserType =
  (typeof DESIGNER_USER_TYPES)[keyof typeof DESIGNER_USER_TYPES];

export const RIGHT_TABS = Object.freeze({
  STYLE: 'style',
  SETTINGS: 'settings',
  INTERACTIONS: 'interactions',
});

export type RightTab = (typeof RIGHT_TABS)[keyof typeof RIGHT_TABS];

export const PROPS_TAB = 'props';
export type Tab = RightTab | typeof PROPS_TAB;

export const ALL_AVAILABLE_ELEMENT_PRESETS = 'ALL_AVAILABLE_ELEMENT_PRESETS';

export const DESIGNER_SITE_MODES = Object.freeze({
  DESIGN: 'design',
  EDIT: 'edit',
  VIEW: 'view',
});

export type DesignerSiteMode =
  (typeof DESIGNER_SITE_MODES)[keyof typeof DESIGNER_SITE_MODES];

/**
 * The height of the Top Bar
 */
export const TOP_BAR_HEIGHT = 40;

/**
 * The height of the Top Bar in comfortable spacing mode (campaign sites).
 */
export const TOP_BAR_HEIGHT_COMFORTABLE = 48;

/**
 * CSS custom property for the topbar height. Resolves to 40px by default
 * and 48px when `html.avoid-left-sidebar--comfortable` is set (campaign sites).
 * Use in emotionStyled `top` / `height` rules so the value tracks the
 * CSS cascade without a JS re-render.
 */
export const TOP_BAR_HEIGHT_CSS_VAR = 'var(--wf-designer--topBarHeight)';

export const getTopBarHeight = (
  spacing: 'default' | 'comfortable' = 'default'
) => {
  return spacing === 'comfortable'
    ? TOP_BAR_HEIGHT_COMFORTABLE
    : TOP_BAR_HEIGHT;
};

/**
 * The height of the Canvas Bar
 */
export const CANVAS_BAR_HEIGHT = 32;
export const CANVAS_BAR_HEIGHT_LARGE = 41; // for screens > 1100px

const CANVAS_BAR_HEIGHT_BREAKPOINT = 1100; // Screen height breakpoint for canvas bar sizing

export const getCanvasBarHeight = (canvasBarPosition: CanvasBarPosition) => {
  // Early returns for cases that always use small height
  const shouldUseSmallHeight =
    typeof window === 'undefined' || // Server-side rendering fallback
    canvasBarPosition === 'bottom'; // Bottom positioned canvas bar

  if (shouldUseSmallHeight) {
    return CANVAS_BAR_HEIGHT;
  }

  // For top positioned canvas bar, use responsive sizing based on screen height
  return window.innerHeight <= CANVAS_BAR_HEIGHT_BREAKPOINT
    ? CANVAS_BAR_HEIGHT
    : CANVAS_BAR_HEIGHT_LARGE;
};
